import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, effect, inject, NgZone, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MdbButtonComponent } from '../../utils/mdb-button/mdb-button.component';
import { MdbCollapseModule } from 'mdb-angular-ui-kit/collapse';
import { MdbRippleModule } from 'mdb-angular-ui-kit/ripple';
import { MdbSidenavComponent, MdbSidenavModule } from 'mdb-angular-ui-kit/sidenav';
import { TranslateModule } from '@ngx-translate/core';
import { SidenavItemComponent } from './sidenav-item/sidenav-item.component';
import { finalize, fromEvent } from 'rxjs';

import { BreadcrumbsComponent } from '../../utils/breadcrumbs/breadcrumbs.component';
import { LanguageSelectComponent } from '../../utils/language-select/language-select.component';
import { AuthService } from '../../../services/auth/auth.service';
import { HttpErrorsService } from '../../../services/utils/http-errors.service';
import { SidenavService } from '../../../services/utils/sidenav.service';
import { Role } from '../../../enums/role';

@Component({
  selector: 'app-sidenav',
  imports: [
    MdbButtonComponent,
    MdbCollapseModule,
    MdbRippleModule,
    MdbSidenavModule,
    TranslateModule,
    SidenavItemComponent,
    BreadcrumbsComponent,
    LanguageSelectComponent
  ],
  templateUrl: './sidenav.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './sidenav.component.scss'
})
export class SidenavComponent implements AfterViewInit {
  @ViewChild('sidenav', { static: true }) sidenav!: MdbSidenavComponent;

  private readonly destroyRef = inject(DestroyRef);

  private readonly baseItems = [
    { text: 'MAILARCHIV.NAV_SEARCH', path: '/search' },
  ];
  private readonly adminItems = [
    { text: 'ADMIN.NAV_USERS', path: '/admin/users' },
    { text: 'ADMIN.NAV_MAIL_ACCOUNTS', path: '/admin/mail-accounts' },
    { text: 'ADMIN.NAV_SETTINGS', path: '/admin/settings' },
  ];
  items = [...this.baseItems];
  activeItem = this.items[0];

  logoutLoading = false;

  constructor(
    public sidenavService: SidenavService,
    private ngZone: NgZone,
    private httpErrorService: HttpErrorsService,
    private authService: AuthService,
  ) {
    effect(() => {
      const isAdmin = this.authService.user()?.role === Role.ADMIN;
      this.items = isAdmin
        ? [...this.baseItems, ...this.adminItems]
        : [...this.baseItems];
      if (!this.items.includes(this.activeItem)) {
        this.activeItem = this.items[0];
      }
    });

    effect(() => {
      if (this.sidenavService.isHidden()) {
        setTimeout(() => {
          this.sidenav.hide();
        }, 0);
      } else {
        setTimeout(() => {
          this.sidenav.show();
        }, 0);
      }
    });
  }

  ngAfterViewInit() {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
        if (window.innerWidth < this.sidenavService.breakWidth && !this.sidenavService.isOverMode()) {
          this.ngZone.run(() => {
            this.sidenavService.isOverMode.set(true);
            this.sidenavService.hide();
          });
        } else if (window.innerWidth >= this.sidenavService.breakWidth && this.sidenavService.isOverMode()) {
          this.ngZone.run(() => {
            this.sidenavService.isOverMode.set(false);
            this.sidenavService.show();
          });
        }
      });
    });
  }

  itemClick(item: { text: string; path: string }) {
    this.activeItem = item;
    // this.breadcrumbService.clearBreadcrumbs(item)
    if (this.sidenavService.isOverMode())
      this.sidenavService.hide();
  }

  logout() {
    this.logoutLoading = true;
    this.authService.logout()
      .pipe(
        finalize(() => this.logoutLoading = false),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.authService.user.set(null);
          window.location.href = '';
        },
        error: (err) => {
          this.httpErrorService.handleError(err);
        }
      });
  }
}
